import { Action, createReducer, on } from '@ngrx/store';
import {
    addressesLoadedFromStorage,
    addressStorageSyncError,
    addressUpserted,
    deleteAddressRequested,
    editAddressRequested,
    hideAddressRequested,
    initAddresses,
    resetAddressVisibility,
    restoreAddressRequested,
    saveAddressesRequested,
    unhideAddressRequested,
} from '../actions/address.actions';
import {
    AddressCollectionState,
    AddressEdit,
    AddressState,
    initialAddressCollectionState,
    withFlagDefaults,
} from '../address-collection.state';
import { hasBackendAddrNumber, isUserCreatedAddress, withAddressNumbers } from '../address-number';

const setSyncingReducer = (state: Readonly<AddressCollectionState>): Readonly<AddressCollectionState> => ({
    ...state,
    status: 'syncing',
});

// Every collection entering the slice passes through here — storage hydration and every write
// alike — which is what makes `withAddressNumbers`' invariant (an id and an `addrNumber` on every
// address) and `withFlagDefaults`' (a `deleted` and an `outOfScope` flag on every address) hold for
// the slice as a whole rather than for whichever write remembered to restore them.
const setAddressesReducer = (
    state: Readonly<AddressCollectionState>,
    props: { addresses: AddressState[] },
): Readonly<AddressCollectionState> => ({
    ...state,
    addresses: withFlagDefaults(withAddressNumbers(props.addresses)),
    status: 'ready',
    error: null,
});

// Status still becomes 'ready' on error so `refreshAddresses()` (and thereby app
// init) can never hang; the previous collection is retained, matching the storage
// layer's own degrade-to-empty behaviour.
const setErrorReducer = (
    state: Readonly<AddressCollectionState>,
    props: { error: string },
): Readonly<AddressCollectionState> => ({
    ...state,
    status: 'ready',
    error: props.error,
});

/**
 * Clears the `visible` flag of the address with the given id — the slice-unique
 * identity that, unlike addrKey, distinguishes manually created addresses. The facade resolved the id against the collection before
 * dispatching, so a missing address is a defensive no-op.
 *
 * @param state the address slice
 * @param props the action payload
 * @param props.id the id of the address to hide
 * @returns the slice with the address hidden
 */
const hideAddressReducer = (
    state: Readonly<AddressCollectionState>,
    props: { id: string },
): Readonly<AddressCollectionState> => {
    if (!state.addresses.some((address) => address.id === props.id)) {
        return state;
    }
    return {
        ...state,
        addresses: state.addresses.map((address) =>
            address.id === props.id ? { ...address, visible: false } : address,
        ),
    };
};
const unhideAddressReducer = (
    state: Readonly<AddressCollectionState>,
    props: { id: string },
): Readonly<AddressCollectionState> => {
    if (!state.addresses.some((address) => address.id === props.id)) {
        return state;
    }
    return {
        ...state,
        addresses: state.addresses.map((address) =>
            address.id === props.id ? { ...address, visible: true } : address,
        ),
    };
};

// Un-hiding is not un-deleting: this is the address-book "show hidden addresses again"
// affordance, not a general "undo whatever happened to this address" button. A tombstoned
// address stays tombstoned through a reset — only a backend re-send (`upsertAddress`) clears it.
const resetAddressVisibilityReducer = (state: Readonly<AddressCollectionState>): Readonly<AddressCollectionState> => ({
    ...state,
    addresses: state.addresses.map((address) => (address.visible ? address : { ...address, visible: true })),
});

/**
 * Tombstones the address with the given id rather than removing it from the collection: a
 * timesheet entry stores only an address id (`TimesheetEntryState.address`), so dropping the
 * address outright would strand every entry that still points at it — including a submitted
 * timesheet's, whose address must keep resolving for the legacy model bridge and the submit
 * out-model. Marking it `deleted` instead keeps it resolvable via `selectAddressesById` while
 * `selectVisibleAddresses` keeps it out of the pickers. An unknown id is a defensive no-op that
 * keeps the collection reference.
 *
 * Only an address the technician typed is deletable at all. A backend address — a customer address
 * or an `AddressSet` row alike — is the backend's to send and to withdraw: deleting it would take it
 * out of the pickers until the backend stops sending it, which is a decision the address book does
 * not get to make. Hiding is the affordance for those (`hideAddressRequested`), and it is
 * recoverable. The same reasoning, and the same pair of tests, as `editAddressReducer` — and it is
 * what `AddressPickerComponent.canDelete` already offers, stated here so it holds for every caller
 * of the action rather than for the one that happens to ask politely.
 *
 * @param state the address slice
 * @param props the action payload
 * @param props.id the id of the address to tombstone
 * @returns the slice with the address marked deleted
 */
const deleteAddressReducer = (
    state: Readonly<AddressCollectionState>,
    props: { id: string },
): Readonly<AddressCollectionState> => {
    const known = state.addresses.find((address) => address.id === props.id);
    if (!known || !isUserCreatedAddress(known)) {
        return state;
    }
    return {
        ...state,
        addresses: state.addresses.map((address) =>
            address.id === props.id ? { ...address, deleted: true } : address,
        ),
    };
};

/**
 * Applies an edit's changed fields to the address with the given id, in place.
 *
 * An unknown id is a defensive no-op, matching every other targeted write in this reducer. An
 * address the backend already knows is a no-op for a different reason: its fields are the
 * backend's to say, not the form's, and the next load would fold the backend's version straight
 * back over the edit anyway (`upsertAddress` matches a backend-known address by its `addrNumber`),
 * so accepting the edit here would only look like it worked before the next sync undid it. Both
 * checks — `isFromAddressSet` and `hasBackendAddrNumber` — are needed: a notification's long
 * customer form is not yet in the address set but still carries a real backend address number, so
 * `isFromAddressSet` alone would let it through.
 *
 * @param state the address slice
 * @param props the action payload
 * @param props.id the id of the address to edit
 * @param props.changes the fields the technician retyped
 * @returns the slice with the address's editable fields changed
 */
const editAddressReducer = (
    state: Readonly<AddressCollectionState>,
    props: { id: string; changes: AddressEdit },
): Readonly<AddressCollectionState> => {
    const known = state.addresses.find((address) => address.id === props.id);
    if (!known || known.isFromAddressSet || hasBackendAddrNumber(known.addrNumber)) {
        return state;
    }
    return {
        ...state,
        addresses: state.addresses.map((address) => (address === known ? { ...address, ...props.changes } : address)),
    };
};

const restoreAddressReducer = (
    state: Readonly<AddressCollectionState>,
    props: { id: string },
): Readonly<AddressCollectionState> => {
    if (!state.addresses.some((address) => address.id === props.id)) {
        return state;
    }
    return {
        ...state,
        addresses: state.addresses.map((address) =>
            address.id === props.id ? { ...address, deleted: false } : address,
        ),
    };
};

const createAddressReducer = createReducer(
    initialAddressCollectionState,
    on(initAddresses, setSyncingReducer),
    on(addressesLoadedFromStorage, setAddressesReducer),
    // The store is the source of truth: the write itself sets the collection (store-first),
    // then the persist effect mirrors it to storage. `addressesSaved` is now a settle-only
    // signal for the facade, so it no longer touches the slice.
    on(saveAddressesRequested, setAddressesReducer),
    on(addressUpserted, setAddressesReducer),
    on(hideAddressRequested, hideAddressReducer),
    on(unhideAddressRequested, unhideAddressReducer),
    on(resetAddressVisibility, resetAddressVisibilityReducer),
    on(deleteAddressRequested, deleteAddressReducer),
    on(editAddressRequested, editAddressReducer),
    on(restoreAddressRequested, restoreAddressReducer),
    on(addressStorageSyncError, setErrorReducer),
);

export function addressReducer(
    state: Readonly<AddressCollectionState>,
    action: Action,
): Readonly<AddressCollectionState> {
    return createAddressReducer(state, action);
}
